import React, { useCallback, useEffect, useState } from 'react';
// sonner — INTE react-toastify: appen monterar bara sonners <Toaster>, så
// toastify-anrop försvann spårlöst. På den ÅRL 8:3-bärande bekräftelsen
// gjorde det varje 409/422 osynlig för användaren (granskningsfynd YE-C-13).
import { toast } from 'sonner';
import { Building2, CheckCircle2, Undo2 } from 'lucide-react';
import * as YearEndService from '../../../../services/YearEndService';

/**
 * Steg 7 — Bolagsverket. Tills API-inlämningen (Gate A1) finns lämnar
 * användaren in årsredovisningen själv och BEKRÄFTAR inlämningen här med
 * datum + kanal. Bekräftelsen sparas som inlämningsbevis
 * (year_end_filing_artifacts/_submissions, filing_type ARSREDOVISNING) och
 * är den enda vägen att markera steget som klart (Codex-blocker 2,
 * 2026-08-11; ÅRL 8 kap. 3 §: inom en månad efter stämmans
 * fastställelsebeslut).
 *
 * Kanalärlighet (Codex-fynd 6, 2026-08-12): Bolagsverkets DIGITALA
 * inlämning tar emot iXBRL via anslutet program — en PDF kan inte lämnas
 * digitalt, så vår PDF kan bara ha postats på papper. Kanalerna är därför
 * "papper" (PDF-kopian med fastställelseintyg ÄR det som postas) och
 * "annat sätt" (t.ex. ombud med egen programvara) med obligatorisk
 * beskrivning. Reviderat bolag intygar att revisionsberättelsen följde med
 * (ÅRL 8:3).
 */
const CHANNEL_LABELS = {
    paper: 'papper',
    other: 'annat sätt',
    bolagsverket_eservice: 'Bolagsverkets e-tjänst', // historiska bevisrader
};

const BolagsverketStep = ({
    companyId,
    processId,
    completed,
    onComplete,
    onUndo,
    busy,
    gateError,
}) => {
    const [evidence, setEvidence] = useState(null);
    const [loadError, setLoadError] = useState(null);
    const [harRevisor, setHarRevisor] = useState(false);

    const [channel, setChannel] = useState('paper');
    const [submittedAt, setSubmittedAt] = useState('');
    const [receiptReference, setReceiptReference] = useState('');
    const [note, setNote] = useState('');
    const [revisionsberattelseBifogad, setRevisionsberattelseBifogad] = useState(false);
    const [confirming, setConfirming] = useState(false);

    const fetchEvidence = useCallback(async () => {
        if (!processId) return;
        setLoadError(null);
        try {
            const resp = await YearEndService.getFilingEvidence(companyId, processId);
            setEvidence(resp?.data || null);
        } catch (err) {
            setLoadError(err.response?.data?.message || 'Kunde inte hämta inlämningsbeviset.');
        }
        try {
            const reportResp = await YearEndService.getAnnualReport(companyId, processId);
            setHarRevisor(reportResp?.data?.report?.fb_sections?.revisor?.harRevisor === true);
        } catch (err) {
            // Revisorflaggan är en UI-hjälp — servern upprätthåller kravet ändå.
        }
    }, [companyId, processId]);

    useEffect(() => { fetchEvidence(); }, [fetchEvidence]);

    const submission = evidence?.latestArsredovisningSubmission || null;

    const handleConfirm = async () => {
        setConfirming(true);
        try {
            await YearEndService.confirmBolagsverketFiling(companyId, processId, {
                channel,
                submittedAt,
                receiptReference: receiptReference.trim() || null,
                note: note.trim() || null,
                revisionsberattelseBifogad: harRevisor ? revisionsberattelseBifogad : undefined,
            });
            toast.success('Inlämningen till Bolagsverket är registrerad.');
            await fetchEvidence();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Kunde inte registrera inlämningen.');
        } finally {
            setConfirming(false);
        }
    };

    return (
        <>
            <style>{`
                .ye-bv-intro { color: #4b5563; font-size: 0.875rem; line-height: 1.6; max-width: 72ch; }
                .ye-bv-form { display: flex; flex-wrap: wrap; gap: 1rem; align-items: flex-end; margin-top: 1rem; }
                .ye-bv-form label { display: flex; flex-direction: column; gap: 0.35rem; font-size: 0.8125rem; color: #374151; }
                .ye-bv-form input, .ye-bv-form select, .ye-bv-form textarea {
                    border: 1px solid #d1d5db; border-radius: 8px; padding: 0.45rem 0.6rem; font-size: 0.875rem;
                }
                .ye-bv-check { display: flex; gap: 0.5rem; align-items: flex-start; margin-top: 0.75rem;
                    font-size: 0.8125rem; color: #374151; max-width: 72ch; }
                .ye-bv-confirmed {
                    display: flex; gap: 0.75rem; align-items: flex-start;
                    background: var(--rgba-primary-1); border-radius: 10px; padding: 1rem;
                    color: #374151; font-size: 0.875rem; margin-top: 1rem;
                }
                .ye-bv-confirmed svg { color: var(--primary); flex-shrink: 0; margin-top: 2px; }
            `}</style>
            <div className="modern-card-body">
                <p className="ye-bv-intro">
                    <Building2 size={16} style={{ verticalAlign: 'text-bottom', marginRight: 6 }} />
                    Alla aktiebolag ska lämna in årsredovisningen till Bolagsverket —
                    en bestyrkt kopia med fastställelseintyg ska ha kommit in inom
                    en månad efter att stämman fastställde resultat- och
                    balansräkningen (8 kap. 3 § årsredovisningslagen; förseningsavgift
                    från sju månader efter räkenskapsårets slut).
                </p>
                <p className="ye-bv-intro" style={{ marginTop: '0.5rem' }}>
                    Så här gör du: ladda ner PDF:en nedan — den innehåller
                    fastställelseintyget på framsidan eftersom stämman är registrerad.
                    Skriv ut kopian, låt en styrelseledamot eller VD underteckna
                    fastställelseintyget{harRevisor ? ', bifoga revisionsberättelsen' : ''} och
                    posta till Bolagsverket. Digital inlämning kräver iXBRL via
                    anslutet program och stöds inte ännu — den kommer med
                    Bolagsverkets API-anslutning.
                </p>

                {loadError && (
                    <div className="alert alert-danger mt-3" style={{ fontSize: '0.875rem' }}>{loadError}</div>
                )}
                {gateError?.message && (
                    <div className="alert alert-warning mt-3" style={{ fontSize: '0.875rem' }}>{gateError.message}</div>
                )}

                {!submission && (
                    <p className="ye-bv-intro" style={{ marginTop: '0.75rem' }}>
                        <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm"
                            onClick={async () => {
                                try {
                                    const name = await YearEndService.downloadAnnualReportPdf(companyId, processId);
                                    if (name) toast.success(`${name} laddades ner.`);
                                } catch (err) {
                                    toast.error('Kunde inte ladda ner årsredovisningen.');
                                }
                            }}
                        >
                            Ladda ner årsredovisningen (PDF)
                        </button>
                        <span style={{ marginLeft: 8, color: '#6b7280', fontSize: '0.8125rem' }}>
                            Bekräftelsen binds till exakt den version du laddar ner här —
                            ladda ner, lämna in den, och bekräfta sedan.
                        </span>
                    </p>
                )}
                {submission ? (
                    <div className="ye-bv-confirmed">
                        <CheckCircle2 size={18} />
                        <div>
                            <strong>Inlämningen är registrerad.</strong>{' '}
                            {String(submission.submitted_at).slice(0, 10)} via{' '}
                            {CHANNEL_LABELS[submission.channel] || submission.channel}
                            {submission.receipt_reference ? ` · ärende ${submission.receipt_reference}` : ''}.
                            {submission.note ? <div style={{ marginTop: 4 }}>{submission.note}</div> : null}
                            <div style={{ marginTop: 4, color: '#6b7280' }}>
                                Beviset (datum, kanal och vilken årsredovisningsversion som avses)
                                är sparat och kan inte lämna processen osynligt ogjort.
                            </div>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="ye-bv-form">
                            <label>
                                Inlämningssätt
                                <select value={channel} onChange={(e) => setChannel(e.target.value)}>
                                    <option value="paper">Papper (den nedladdade kopian)</option>
                                    <option value="other">Annat sätt (t.ex. via ombud)</option>
                                </select>
                            </label>
                            <label>
                                Inlämningsdatum
                                <input
                                    type="date"
                                    value={submittedAt}
                                    max={new Date().toISOString().slice(0, 10)}
                                    onChange={(e) => setSubmittedAt(e.target.value)}
                                />
                            </label>
                            <label>
                                Ärendenummer (frivilligt)
                                <input
                                    type="text"
                                    placeholder="t.ex. 123456/2026"
                                    value={receiptReference}
                                    onChange={(e) => setReceiptReference(e.target.value)}
                                />
                            </label>
                            {channel === 'other' && (
                                <label style={{ flexBasis: '100%' }}>
                                    Hur lämnades årsredovisningen in?
                                    <textarea
                                        rows={2}
                                        placeholder="T.ex. redovisningsbyrån lämnade in digitalt via sin programvara"
                                        value={note}
                                        onChange={(e) => setNote(e.target.value)}
                                    />
                                </label>
                            )}
                        </div>
                        {channel === 'other' && (
                            <p className="ye-bv-intro" style={{ marginTop: '0.5rem', color: '#92400e' }}>
                                Obs: lämnades årsredovisningen in digitalt av någon annan
                                (iXBRL via deras programvara) är det DERAS fil som gavs in —
                                vårt bevis avser innehållet i den här versionen, inte den
                                inlämnade filen. Spara deras kvittens.
                            </p>
                        )}
                        {harRevisor && (
                            <label className="ye-bv-check">
                                <input
                                    type="checkbox"
                                    checked={revisionsberattelseBifogad}
                                    onChange={(e) => setRevisionsberattelseBifogad(e.target.checked)}
                                />
                                <span>
                                    Revisionsberättelsen lämnades in tillsammans med
                                    årsredovisningen (krävs — ÅRL 8 kap. 3 §).
                                </span>
                            </label>
                        )}
                        <div className="ye-bv-form">
                            <button
                                className="btn btn-primary"
                                onClick={handleConfirm}
                                disabled={confirming || !submittedAt
                                    || (channel === 'other' && !note.trim())
                                    || (harRevisor && !revisionsberattelseBifogad)}
                            >
                                {confirming ? 'Registrerar…' : 'Bekräfta inlämningen'}
                            </button>
                        </div>
                    </>
                )}

                <div className="ye-proposal-actions" style={{ marginTop: '1.5rem' }}>
                    {completed ? (
                        <button className="btn btn-outline-secondary d-inline-flex align-items-center gap-2" onClick={onUndo} disabled={busy}>
                            <Undo2 size={16} /> Ångra steget
                        </button>
                    ) : (
                        <button className="btn btn-primary" onClick={onComplete} disabled={busy || !submission}>
                            Markera steget som klart
                        </button>
                    )}
                </div>
            </div>
        </>
    );
};

export default BolagsverketStep;
